import { AxiosError } from 'axios'
import { forceUpdateCookies } from 'webtonative'
import amsAPI, { getApiUrl } from '../../API/amsConfig'
import { popupAPI } from '../../API/popupConfig'
import { ValidationState } from '../../Dialogs/Common/types'
import { SocialType } from '../../SocialLogin/SocialLogin'
import { reportSharedError, reportSharedWarning } from '../../utils/errorReporting'
import { ServerErrors } from '../../utils/serverErrors'
import { FTUE, Rules, Settings, UserData, UserGroup } from '../GlobalHeader'
import GetCookie from '../common/getCookie'
import { createQueryParams } from '../common/queryParams'
import removeCookie from '../common/removeCookie'
import setCookie from '../common/setCookie'
import { decodeJwtExpiry } from './jwt'
import {
    clearNativeSession,
    getStoreAvailability,
    restoreNativeSession,
    storeNativeSession,
} from './nativeSessionStore'
import {
    beginSession,
    clearIdentityTombstone,
    endSession,
    getSessionSeed,
    hasEvidenceOfPriorMemberSession,
    noteAbort,
    noteGuestProvisioned,
    noteNativeRestore,
    noteRenewal,
    SessionSource,
} from './sessionTelemetry'

// -------------------------------------------------------------KEYS-------------------------------------------------------------
const sessionStorageKey = '_icc_user'
export const tokenCookieKey = 'jwt'
export const REFRESH_TOKEN_INTERVAL = 1000 * 60 * 60 * 24 * 7 * 4 // 4 weeks
// A boot that can't confirm the session (network blip, WebView hiccup) gets one
// retry before giving up — long enough to ride out a transient failure, short
// enough not to stall first paint noticeably.
export const ENSURE_LOGIN_RETRY_DELAY_MS = 2000
// Boot-path AMS calls (renew, profile, registerGuest): a hung request would otherwise hold the first render
// until the browser gives up; a timeout turns it into the indeterminate path above.
const AMS_BOOT_TIMEOUT_MS = 10000

// -------------------------------------------------------------HELPER FUNCTIONS-------------------------------------------------------------
function getTokenExpirationDate(token: string, apiUrl: string): Date | null {
    const expirationDate = decodeJwtExpiry(token)
    if (!expirationDate) {
        removeCookie(tokenCookieKey, getCookieDomain(apiUrl))
        return null
    }
    return expirationDate
}

export const getCookieDomain = (apiURL: string) => {
    if (apiURL.endsWith('.iccstage.net')) return '.iccstage.net'
    if (apiURL.endsWith('.chessnclub.net')) return '.chessnclub.net'
    return '.chessclub.com'
}

// One constant message + a `reason` tag for every place a session gets cleared,
// so the actual distribution of kill causes (renewal rejected vs. expired vs.
// explicit logout vs. profile fetch rejected...) is measurable in Sentry instead
// of only inferable from code reading.
// Every session-clearing site funnels through here, so this is also the single
// place that closes out the session's lifetime metric — see sessionTelemetry.
const trackSessionKilled = (reason: string, context?: Record<string, unknown>) => {
    reportSharedWarning('Session cleared', { reason, ...context }, { reason })
    endSession(reason)
}

const isGuestUser = (user: { groups?: Array<{ name: string }> } | undefined): boolean =>
    !!user?.groups?.some((group) => group.name === 'guest')

// Records the session that an auth mutation just established. `mirrorCurrentCookie`
// handles the native mirror; this handles the telemetry clock and correlation id.
const startSessionFromResponse = (
    source: SessionSource,
    data: { user?: { id: string; groups?: Array<{ name: string }> } },
) => {
    if (!data?.user?.id) return
    // A new identity is now live. Any session check already in flight — most
    // commonly the appResume-triggered one that started when a native SSO sheet
    // closed — fetched the PREVIOUS identity's profile and must not be allowed
    // to apply it over this one (see fetchEnsureLogin's epoch check).
    sessionEpoch += 1
    const isGuest = isGuestUser(data.user)
    // A real identity is established again, so any older tombstone is moot and
    // must not later be mistaken for an account that silently became a guest.
    if (!isGuest) clearIdentityTombstone()
    beginSession({ source, userId: data.user.id, isGuest })
}

// AxiosError carries the live request config, including the Authorization
// header amsConfig.ts's interceptor set — passing it unmodified to
// console.error/Sentry would leak the raw session JWT into telemetry. Strip it
// down to the fields actually useful for debugging before it's ever reported.
const sanitizeAxiosErrorForReporting = (error: unknown): unknown => {
    if (!(error instanceof Error)) return error
    const axiosError = error as AxiosError
    if (!axiosError.isAxiosError) return error
    const sanitized = new Error(axiosError.message)
    sanitized.name = axiosError.name
    sanitized.stack = axiosError.stack
    return Object.assign(sanitized, {
        code: axiosError.code,
        status: axiosError.response?.status,
        url: axiosError.config?.url,
        method: axiosError.config?.method,
    })
}

// Bumped by logout() so a slow, still-in-flight fetchEnsureLogin() started
// before the tap can tell its own eventual success is stale and must not
// resurrect the just-logged-out account in the store or in native storage.
let sessionEpoch = 0

// Fire-and-forget: a session we've locally determined is dead (bad renewal,
// expired, rejected profile fetch) must not keep surviving in native storage,
// or the next cold start just restores and re-rejects it forever.
const clearNativeSessionOnDeath = (reason: string, apiURL: string) => {
    clearNativeSession().then(({ ok, timedOut }) => {
        // Deliberately NOT trackSessionKilled: the session already ended, and
        // routing this through there would end it a second time and emit a bogus
        // lifetime. clearNativeSession itself already counts the store outcome.
        if (!ok) {
            reportSharedWarning(
                'Native session mirror could not be cleared after session death',
                { apiURL, timedOut, triggeredBy: reason },
                { reason: 'native_mirror_clear_failed' },
            )
        }
    })
}

// Distinct from trackSessionKilled and error-level, not warning: this measures
// specifically when the native mirror *had* a session to offer and it still
// didn't survive the round trip — the case the mirror exists to prevent.
const trackNativeRestoreRejected = (context: Record<string, unknown>) => {
    reportSharedError(
        'Native-mirrored session existed but was rejected on restore',
        new Error('native_restore_rejected'),
        context,
        { reason: 'native_restore_rejected' },
    )
}

/** Mirrors the current session into native storage — a no-op outside a wrapper. */
const mirrorSession = (jwt: string, userId: string, apiURL: string) => {
    // The telemetry seed rides along so a session restored on the next cold start
    // keeps its original start time instead of resetting its measured lifetime.
    const seed = getSessionSeed()
    storeNativeSession({ jwt, userId, origin: apiURL, sessionId: seed?.sessionId, startedAt: seed?.startedAt })
}

// AMS delivers the token via Set-Cookie, not in these endpoints' response
// bodies, and the browser applies it before this callback runs — so reading it
// back off the cookie right after a successful call is the reliable way to get
// the token that was just established, for every login/signup/social/guest path.
const mirrorCurrentCookie = (userId: string) => {
    const jwt = GetCookie(tokenCookieKey)
    if (jwt) mirrorSession(jwt, userId, getApiUrl())
}

// Writes the cookie with the attributes a *restored* token needs that a
// server-issued one already has: SameSite=None + Secure are required for it to
// ride the cross-site withCredentials XHRs to AMS (amsConfig.ts) — a cookie set
// without them simply won't be sent, and the restore would look successful
// while every subsequent request 401s.
const writeCookieFromRestoredToken = (jwt: string, apiURL: string): boolean => {
    const expiry = decodeJwtExpiry(jwt)
    if (!expiry) return false
    setCookie(tokenCookieKey, jwt, getCookieDomain(apiURL), expiry, { secure: true, sameSite: 'None' })
    return true
}

export const errorHandler = (error: any) => {
    if (error instanceof Error) {
        const axiosError = error as AxiosError
        return {
            error: {
                // Absent on a pure network failure (no response reached the client at
                // all) — callers that need to tell "confirmed unauthorized" apart from
                // "couldn't confirm" branch on this rather than on code/message.
                status: axiosError.response?.status,
                code:
                    axiosError.response &&
                    axiosError.response.data &&
                    typeof axiosError.response.data === 'object' &&
                    'code' in axiosError.response.data
                        ? axiosError.response.data['code']
                        : 'SOMETHING_WENT_WRONG',
                message:
                    axiosError.response &&
                    axiosError.response.data &&
                    typeof axiosError.response.data === 'object' &&
                    'message' in axiosError.response.data &&
                    typeof axiosError.response.data['message'] === 'string'
                        ? axiosError.response.data['message']
                        : 'Something went wrong, please try again later.',
                details:
                    axiosError.response &&
                    axiosError.response.data &&
                    typeof axiosError.response.data === 'object' &&
                    'details' in axiosError.response.data
                        ? axiosError.response.data['details']
                        : null,
            },
        }
    } else {
        // callers render message as a React child, so it must never be a raw event/object
        const message =
            typeof error === 'string'
                ? error
                : typeof error?.message === 'string'
                  ? error.message
                  : 'Something went wrong, please try again later.'
        return { error: { message } }
    }
}

// -------------------------------------------------------------LOGIN FLOW-------------------------------------------------------------
export const login = async (identifier: string, password: string) => {
    try {
        const response = await amsAPI.post('/login', { identifier, password })
        sessionStorage.setItem(sessionStorageKey, JSON.stringify(response.data))
        // No-ops on iOS/web (the SDK gates itself to Android) — forces the just-set
        // cookie to disk immediately rather than waiting for Android WebView's lazy
        // flush, which can lose it if the process dies before flushing on its own.
        forceUpdateCookies()
        // Before mirroring: the mirror carries the telemetry seed this creates.
        startSessionFromResponse('login', response.data)
        mirrorCurrentCookie(response.data.user.id)
        return response.data
    } catch (error) {
        return errorHandler(error)
    }
}

// A session-clearing outcome ('cleared') is deliberately distinct from an
// unresolved one ('indeterminate'): a WebView network blip or a 5xx must NOT be
// treated the same as a confirmed-expired/rejected token, or a transient hiccup
// silently logs the user out (previously ams-functions' single biggest suspect
// for "random" session loss — see docs/native-session-persistence-adr.md).
export type RenewTokenResult = { status: 'ok'; token: string } | { status: 'cleared' } | { status: 'indeterminate' }

export const renewToken = async (token: string, apiURL: string): Promise<RenewTokenResult> => {
    if (!token) return { status: 'cleared' }
    if (!navigator.onLine) {
        noteAbort('renew', 'offline')
        return { status: 'indeterminate' }
    }

    const expirationDate = getTokenExpirationDate(token, apiURL)
    if (!expirationDate) {
        sessionStorage.setItem(sessionStorageKey, '')
        trackSessionKilled('malformed_jwt', { apiURL })
        clearNativeSessionOnDeath('malformed_jwt', apiURL)
        return { status: 'cleared' }
    }
    const expirationTimeLeft = expirationDate.getTime() - new Date().getTime()

    if (expirationTimeLeft < 0) {
        sessionStorage.setItem(sessionStorageKey, '')
        removeCookie(tokenCookieKey, getCookieDomain(apiURL))
        trackSessionKilled('renew_expired', { apiURL })
        clearNativeSessionOnDeath('renew_expired', apiURL)
        return { status: 'cleared' }
    }

    if (expirationTimeLeft < REFRESH_TOKEN_INTERVAL) {
        try {
            const response = await amsAPI.put('/api/user/renew', undefined, { timeout: AMS_BOOT_TIMEOUT_MS })
            forceUpdateCookies()
            noteRenewal()
            return { status: 'ok', token: response.data.jwt }
        } catch (error) {
            const httpStatus = (error as AxiosError)?.response?.status
            if (httpStatus === 401 || httpStatus === 403) {
                reportSharedError('Token renewal rejected, clearing session', sanitizeAxiosErrorForReporting(error), {
                    flow: 'auth',
                    apiURL,
                })
                sessionStorage.setItem(sessionStorageKey, '')
                removeCookie(tokenCookieKey, getCookieDomain(apiURL))
                const killReason = httpStatus === 401 ? 'renew_401' : 'renew_403'
                trackSessionKilled(killReason, { apiURL })
                clearNativeSessionOnDeath(killReason, apiURL)
                return { status: 'cleared' }
            }
            // Network error, timeout, or 5xx: the cookie is still valid as far as we
            // know — a blip must not clear a live session.
            noteAbort('renew', httpStatus ? `http_${httpStatus}` : 'network')
            return { status: 'indeterminate' }
        }
    }
    // No renewal needed yet — the existing token is still valid.
    return { status: 'ok', token }
}

export type EnsureLoginResult = {
    user: UserData | null
    /** True when the auth state genuinely could not be confirmed this attempt
     *  (network/5xx) — callers must not guest-register or log out on this, only
     *  on a definite `user: null, indeterminate: false`. */
    indeterminate: boolean
    /** True when a login or logout landed while this check was in flight: the
     *  result describes a session that no longer exists. Callers must treat it
     *  as a pure no-op — neither apply it nor react to it. Always paired with
     *  `indeterminate: true` so a caller that ignores this flag still can't
     *  mistake it for "confirmed no session". */
    stale?: boolean
}

// `epochAtStart` is the epoch the *whole* check began under — including a
// retry, which must not re-read it: a retry that started fresh inside a
// logout's window would capture the post-logout epoch and sail past the
// staleness check below while still holding the pre-logout cookie.
const fetchEnsureLogin = async (epochAtStart: number): Promise<EnsureLoginResult> => {
    const apiURL = getApiUrl()
    let token = GetCookie(tokenCookieKey)

    // No cookie — before concluding "logged out", check whether the wrapper's
    // native storage still has a session the WebView's cookie jar lost (WKWebView
    // flakiness, ITP's cap on JS-set cross-site cookies, Android's lazy flush; see
    // docs/native-session-persistence-adr.md). A no-op outside a wrapper.
    let restoredUserId: string | null = null
    let restoredSeed: { sessionId: string; startedAt: number } | null = null
    if (!token) {
        const restored = await restoreNativeSession(apiURL)
        // A session landed while the native lookup was pending (e.g. an appResume check racing a guest
        // registration or login): the cookie read above predates it, so neither "confirmed no session"
        // nor restoring over the new cookie is safe — the flow that landed applies its own session.
        if (sessionEpoch !== epochAtStart) return { user: null, indeterminate: true, stale: true }
        if (restored && writeCookieFromRestoredToken(restored.jwt, apiURL)) {
            token = restored.jwt
            restoredUserId = restored.userId
            // Carried into beginSession below so the restored session resumes the
            // original clock instead of looking brand new on every cold start.
            restoredSeed =
                restored.sessionId && typeof restored.startedAt === 'number'
                    ? { sessionId: restored.sessionId, startedAt: restored.startedAt }
                    : null
            noteNativeRestore(restored.savedAt ?? null)
        } else if (getStoreAvailability() === 'unavailable' && hasEvidenceOfPriorMemberSession()) {
            noteAbort('native_restore', 'bridge_timeout')
            // The bridge didn't answer, so this isn't a confirmed "no session" —
            // it's "couldn't check" — and this device has held a real account
            // before, so guest-registering now could orphan it. Hold off.
            //
            // Without that evidence we deliberately fall through to normal guest
            // provisioning: on a shell that lacks the bridge entirely (old iOS
            // shells accept the postMessage and never answer) a timeout happens on
            // EVERY boot, and treating it as unconfirmed would mean a fresh
            // install never gets an identity at all — a lockout, not a safeguard.
            return { user: null, indeterminate: true }
        }
    }

    if (!token) {
        return { user: null, indeterminate: false }
    }

    const renewal = await renewToken(token, apiURL)
    if (renewal.status === 'cleared') {
        if (restoredUserId) trackNativeRestoreRejected({ apiURL, userId: restoredUserId, stage: 'renew' })
        return { user: null, indeterminate: false }
    }
    if (renewal.status === 'indeterminate') {
        return { user: null, indeterminate: true }
    }

    const profile = await loadUserProfileData()

    // A logout OR a newer login landed while this request was in flight — its
    // result describes a session that no longer exists. Applying it would either
    // resurrect the account the user just logged out of, or — the native-SSO
    // case seen on QA — overwrite a just-established member with the stale
    // guest profile this check fetched before the sheet closed. Undo only our
    // own side effect: a renew whose Set-Cookie re-planted a token that logout
    // had already cleared. Never touch a cookie we didn't issue — after a login
    // it belongs to the new member.
    if (sessionEpoch !== epochAtStart) {
        if (renewal.token !== token && GetCookie(tokenCookieKey) === renewal.token) {
            removeCookie(tokenCookieKey, getCookieDomain(apiURL))
        }
        return { user: null, indeterminate: true, stale: true }
    }

    if (profile && !profile.error) {
        sessionStorage.setItem(sessionStorageKey, JSON.stringify({ user: profile }))
        // Recorded here rather than at restore time because only the profile tells
        // us whether this is a guest. beginSession continues the existing clock
        // when the user id matches (cold start whose cookie survived) or when a
        // seed came back from the mirror, and starts a fresh one otherwise.
        const isGuest = isGuestUser(profile)
        if (!isGuest) clearIdentityTombstone()
        beginSession({
            source: restoredUserId ? 'native_restore' : 'cookie_resume',
            userId: profile.id,
            isGuest,
            seed: restoredSeed,
        })
        mirrorSession(renewal.token, profile.id, apiURL)
        return { user: profile, indeterminate: false }
    }

    const status = profile?.error?.status
    if (status === 401 || status === 403) {
        sessionStorage.setItem(sessionStorageKey, '')
        removeCookie(tokenCookieKey, getCookieDomain(apiURL))
        trackSessionKilled('profile_401', { apiURL, status })
        clearNativeSessionOnDeath('profile_401', apiURL)
        if (restoredUserId) trackNativeRestoreRejected({ apiURL, userId: restoredUserId, stage: 'profile' })
        return { user: null, indeterminate: false }
    }

    // Network/5xx/unknown failure fetching the profile — the cookie is still
    // there and still unverified either way; do not treat this boot as
    // logged-out (that path used to silently mint a fresh guest — ADR finding #5).
    noteAbort('profile', status ? `http_${status}` : 'network')
    return { user: null, indeterminate: true }
}

const runEnsureLogin = async (): Promise<EnsureLoginResult> => {
    // Captured once for both attempts — see fetchEnsureLogin's parameter note.
    const epochAtStart = sessionEpoch
    const result = await fetchEnsureLogin(epochAtStart)
    if (!result.indeterminate || result.stale) return result

    await new Promise((resolve) => setTimeout(resolve, ENSURE_LOGIN_RETRY_DELAY_MS))
    // A login or logout landed during the first attempt or the wait — the
    // retry would only be checking a session that has since been replaced.
    if (sessionEpoch !== epochAtStart) return { user: null, indeterminate: true, stale: true }
    return fetchEnsureLogin(epochAtStart)
}

let inFlightEnsureLogin: Promise<EnsureLoginResult> | null = null

/**
 * Reads the session cookie and confirms it with AMS. A single retry rides out
 * one transient failure (a WebView cold-start network blip) before a genuinely
 * indeterminate result is handed back — callers must not guest-register or log
 * out on `indeterminate: true`.
 *
 * Concurrent callers (boot's initUser and an appResume-triggered initUser can
 * legitimately overlap) share one in-flight check rather than each running
 * their own — otherwise two callers landing on "confirmed no session" at
 * nearly the same moment would each independently guest-register.
 */
export const ensureLogin = (): Promise<EnsureLoginResult> => {
    if (!inFlightEnsureLogin) {
        inFlightEnsureLogin = runEnsureLogin().finally(() => {
            inFlightEnsureLogin = null
        })
    }
    return inFlightEnsureLogin
}

export const logout = async (reason: 'explicit' | 'server_push' = 'explicit') => {
    const apiURL = getApiUrl()
    // Invalidates any fetchEnsureLogin() already in flight — see its epoch check —
    // before anything else, so a slow stale check can't win a race against this.
    sessionEpoch += 1
    // Local credentials go FIRST, synchronously: the native clear below can take
    // up to its bridge timeout, and for that whole window a session check that
    // started after the epoch bump could still read a live cookie, renew it, and
    // resurrect the account. Nothing about the native delete needs the cookie
    // to still exist, so there is no reason to let it outlive the bump.
    sessionStorage.setItem(sessionStorageKey, '')
    removeCookie(tokenCookieKey, getCookieDomain(apiURL))
    // Awaited: a session surviving in native storage after logout is a silent
    // future re-login, not a UX nicety to skip. On a bridge timeout it still
    // resolves (never hangs the user in a logged-in state) — see nativeSessionStore.
    const { ok, timedOut } = await clearNativeSession()
    if (!ok) {
        // A warning, not a session-kill: trackSessionKilled below ends the session
        // exactly once, with the real reason.
        reportSharedWarning(
            'Native session mirror could not be cleared on logout',
            { apiURL, timedOut },
            { reason: 'native_mirror_clear_failed' },
        )
    }
    trackSessionKilled(reason === 'explicit' ? 'explicit_logout' : 'server_logout_push')
    //     // TODO: not implemented on backend
    // try {
    // sessionStorage.setItem(sessionStorageKey, '')
    // setCookie(tokenCookieKey, '', getCookieDomain(apiURL))
    //     const response = await fetch(`${apiURL}/logout`, {
    //         method: 'GET',
    //         mode: 'cors',
    //         headers: { 'Content-Type': 'application/json' },
    //         credentials: 'include',
    //     })

    //     const data = await response.json()

    //     if (!response.ok) {
    //         throw new Error(data.message || 'Something went wrong with the logout!')
    //     } else {
    //         sessionStorage.setItem(sessionStorageKey, '')
    //         removeCookie(tokenCookieKey)
    //         return data
    //     }
    // } catch (error) {
    //     return { error: error instanceof Error ? error.message : error }
    // }
}

export const loginSocial = async (
    socialToken: string,
    accessToken: string,
    socialType: SocialType,
    userName?: string,
    country?: string,
    over12?: boolean,
    emailSubscribe?: boolean,
    email?: string,
    firstName?: string,
    lastName?: string,
) => {
    try {
        const response = await amsAPI.post('/loginSocial', {
            social_token: socialToken,
            access_token: accessToken,
            social_network_type: socialType,
            email: email,
            user_name: userName,
            first_name: firstName,
            last_name: lastName,
            over_12_years_old: over12,
            country: country,
            subscribed_to_mailing_list: emailSubscribe,
        })

        sessionStorage.setItem(sessionStorageKey, JSON.stringify(response.data))
        forceUpdateCookies()
        startSessionFromResponse('social', response.data)
        mirrorCurrentCookie(response.data.user.id)

        return response.data
    } catch (error) {
        return errorHandler(error)
    }
}

// -------------------------------------------------------------REGISTRATION FLOW-------------------------------------------------------------
export const signup = async (
    email: string,
    password: string,
    user_name: string,
    country: string,
    over12: boolean,
    emailSubscribe: boolean,
) => {
    try {
        const currentURL = `${window.location.origin}/`
        const successRedirectURL = currentURL
        const failureRedirectURL = `${currentURL}${currentURL.indexOf('?') > -1 ? '&' : '?'}emailConfirmed=false`

        const response = await amsAPI.post('/register', {
            email,
            password,
            user_name,
            country,
            over_12_years_old: over12,
            subscribed_to_mailing_list: emailSubscribe,
            successRedirectURL,
            failureRedirectURL,
        })

        sessionStorage.setItem(sessionStorageKey, JSON.stringify(response.data))
        forceUpdateCookies()
        startSessionFromResponse('signup', response.data)
        mirrorCurrentCookie(response.data.user.id)

        return response.data
    } catch (error) {
        return errorHandler(error)
    }
}

const performGuestRegistration = async () => {
    try {
        const response = await amsAPI.post('/registerGuest', {}, { timeout: AMS_BOOT_TIMEOUT_MS })
        // axios resolves a torn-down request (status 0) with an empty body
        if (!response.data?.user) throw new Error('registerGuest returned no user')

        sessionStorage.setItem(sessionStorageKey, JSON.stringify(response.data))
        forceUpdateCookies()
        noteGuestProvisioned(response.data.user.id)
        startSessionFromResponse('guest', response.data)
        mirrorCurrentCookie(response.data.user.id)
        return response.data
    } catch (error) {
        return errorHandler(error)
    }
}

let inFlightGuestRegistration: ReturnType<typeof performGuestRegistration> | null = null

// Two concurrent callers landing on "confirmed no session" around the same
// moment (e.g. GlobalContext's boot effect and an appResume-triggered
// initUser()) must share one /registerGuest call — otherwise each mints its
// own guest account and whichever response's cookie lands last silently wins.
export const guestRegistration = () => {
    if (inFlightGuestRegistration) {
        // Same account as the caller that started it — only that caller announces it (completeSignup).
        return inFlightGuestRegistration.then((result) => ({ ...result, joined: true }))
    }
    inFlightGuestRegistration = performGuestRegistration().finally(() => {
        inFlightGuestRegistration = null
    })
    return inFlightGuestRegistration
}

export const upgradeGuest = async (
    email: string,
    password: string,
    user_name: string,
    country: string,
    over12: boolean,
    emailSubscribe: boolean,
) => {
    try {
        const currentURL = window.location.href
        const successRedirectURL = currentURL
        const failureRedirectURL = `${currentURL}${currentURL.indexOf('?') > -1 ? '&' : '?'}emailConfirmed=false`

        const response = await amsAPI.post('/api/user/upgradeGuest', {
            email,
            password,
            user_name,
            country,
            over_12_years_old: over12,
            subscribed_to_mailing_list: emailSubscribe,
            successRedirectURL,
            failureRedirectURL,
        })

        sessionStorage.setItem(sessionStorageKey, JSON.stringify(response.data))
        forceUpdateCookies()
        startSessionFromResponse('guest_upgrade', response.data)
        mirrorCurrentCookie(response.data.user.id)

        return response.data
    } catch (error) {
        return errorHandler(error)
    }
}

export const upgradeGuestSocial = async (
    socialToken: string,
    accessToken: string,
    socialType: SocialType,
    userName?: string,
    country?: string,
    over12?: boolean,
    emailSubscribe?: boolean,
) => {
    try {
        const response = await amsAPI.post('/api/user/upgradeGuestSocial', {
            social_token: socialToken,
            access_token: accessToken,
            social_network_type: socialType,
            user_name: userName,
            country,
            over_12_years_old: over12,
            subscribed_to_mailing_list: emailSubscribe,
        })

        sessionStorage.setItem(sessionStorageKey, JSON.stringify(response.data))
        forceUpdateCookies()
        startSessionFromResponse('guest_upgrade', response.data)
        mirrorCurrentCookie(response.data.user.id)

        return response.data
    } catch (error) {
        return errorHandler(error)
    }
}

export const isEmailTaken = async (email: string) => {
    const encodedEmail = encodeURIComponent(email)
    try {
        const response = await amsAPI.get(
            `/isEmailTaken${createQueryParams({
                email: encodedEmail,
            })}`,
        )

        return response.data.taken
    } catch (error) {
        return false
    }
}

export const validateUsername = async (userName: string) => {
    try {
        const response = await amsAPI.get(
            `/usernameTaken${createQueryParams({
                userName,
            })}`,
        )

        if (response.data.taken) {
            return ValidationState.TAKEN
        }

        return ValidationState.FREE
    } catch (error) {
        if (error instanceof Error) {
            const axiosError = error as AxiosError
            if (
                axiosError.response &&
                axiosError.response.data &&
                typeof axiosError.response.data === 'object' &&
                'code' in axiosError.response.data &&
                axiosError.response.data['code'] === ServerErrors.USERNAME_IS_NOT_ALLOWED
            ) {
                return ValidationState.NOT_ALLOWED
            } else {
                return ValidationState.ERROR
            }
        } else {
            return ValidationState.ERROR
        }
    }
}

export const forgotPassword = async (email: string) => {
    try {
        const response = await amsAPI.post('/newPasswordEmail', {
            email: email,
        })
        return response.data
    } catch (error) {
        return errorHandler(error)
    }
}

export const resetPassword = async (token: string, password: string) => {
    try {
        const response = await amsAPI.post('/resetPassword', {
            reset_token: token,
            new_password: password,
        })
        return response.data
    } catch (error) {
        return errorHandler(error)
    }
}

export const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
        const response = await amsAPI.put('/api/user/password', {
            current_password: currentPassword,
            new_password: newPassword,
        })
        return response.data
    } catch (error) {
        return errorHandler(error)
    }
}

export const resendConfirmationEmail = async (email: string) => {
    try {
        const currentURL = window.location.origin + window.location.pathname
        const successRedirectURL = encodeURIComponent(currentURL)
        const failureRedirectURL = encodeURIComponent(`${currentURL}?emailConfirmed=false`)

        const encodedEmail = encodeURIComponent(email)
        const response = await amsAPI.get(
            `/resendConfirmationEmail${createQueryParams({
                email: encodedEmail,
                successRedirectURL,
                failureRedirectURL,
            })}`,
        )

        return response.data
    } catch (error) {
        return errorHandler(error)
    }
}

// -------------------------------------------------------------USER PROFILE-------------------------------------------------------------
export const getUserData = () => {
    if (typeof window === 'undefined') return null
    const userData = sessionStorage.getItem(sessionStorageKey)
    if (!!userData) {
        const parsedData = JSON.parse(userData)
        if (parsedData.error) {
            return null
        }
        return parsedData
    }
    return null
}

export const loadUserProfileData = async () => {
    try {
        const response = await amsAPI.get('/api/user/profile', { timeout: AMS_BOOT_TIMEOUT_MS })
        // axios resolves a torn-down request (status 0) with an empty body
        if (!response.data?.id) throw new Error('profile returned no user')
        return response.data
    } catch (error) {
        reportSharedError('User profile load failed', sanitizeAxiosErrorForReporting(error), {
            endpoint: '/api/user/profile',
        })
        return errorHandler(error)
    }
}

export const updateSessionSettings = (settings: Settings) => {
    const userData = sessionStorage.getItem(sessionStorageKey)
    if (!!userData) {
        const parsedData = JSON.parse(userData)
        parsedData.user.game_settings = settings
        sessionStorage.setItem(sessionStorageKey, JSON.stringify(parsedData))
    }
}

export const updateSessionUser = (user: UserData) => {
    const userData = sessionStorage.getItem(sessionStorageKey)
    if (!!userData) {
        const parsedData = JSON.parse(userData)
        parsedData.user = user
        sessionStorage.setItem(sessionStorageKey, JSON.stringify(parsedData))
    }
}

export const saveUserSettings = async (settings: Settings) => {
    try {
        const response = await amsAPI.put('/api/user/settings', settings)
        updateSessionSettings(settings)
        return response.data
    } catch (error) {
        return errorHandler(error)
    }
}

export const editProfile = async (userData: UserData) => {
    try {
        const response = await amsAPI.put('/api/user/profile', userData)
        return response.data
    } catch (error) {
        return errorHandler(error)
    }
}

export const updateFTUE = async (ftue: Partial<Record<FTUE, boolean>>) => {
    try {
        const response = await amsAPI.patch('/api/user/ftue', ftue)
        return response.data
    } catch (error) {
        return errorHandler(error)
    }
}

export const newContentSeen = async (value: string) => {
    try {
        const response = await amsAPI.patch('/api/user/content', { [value]: false })
        return response.data
    } catch (error) {
        return errorHandler(error)
    }
}

export const getPopup = async (location: string) => {
    try {
        const response = await popupAPI.get(
            `/api/user/popups${createQueryParams({
                url: location,
            })}`,
        )
        return response.data
    } catch (error) {
        return errorHandler(error)
    }
}

export const getUserAccess = (accessRule: Rules) => {
    let isEnabled = false
    const data = getUserData()
    const groups: Array<UserGroup> = data?.user?.groups || null
    if (groups) {
        const ruleNames = groups.flatMap((group) => group.rules.map((rule) => rule.name))
        isEnabled = ruleNames?.some((rule) => rule === Rules.FULL_ACCESS || rule === accessRule) || false
    }

    return isEnabled
}

type UserRating = {
    rating: number
    win: number
    draw: number
    loss: number
    best?: number
    game_type: 'bullet' | 'blitz' | 'rapid' | 'classic'
    best_date?: Date
    total_games: number
    is_provisional: boolean
}

export type UserSearchResult = {
    avatarUrl: string
    country: string
    ratings: {
        blitz: UserRating
        bullet: UserRating
        rapid: UserRating
        classic: UserRating
    }
    title: string
    id: string
    userName: string
    status: 'online' | 'offline'
    botdRewardUrl?: string
    reward: {
        tier: number
        season: string
    }
}

export const getPlayers = async (
    limit: number,
    search: string,
    filter: string,
): Promise<UserSearchResult[] | false> => {
    try {
        const response = await amsAPI.get(
            `/api/user/players${createQueryParams({
                limit,
                search,
                filter,
            })}`,
        )
        return response.data.users
    } catch (error) {
        return false
    }
}

export interface IAccountDeleteConfig {
    timeHours: number
}

export const getAccountDeleteConfig = async (): Promise<IAccountDeleteConfig> => {
    try {
        const response = await amsAPI.get('/api/user/getAccountDeleteConfig')
        return response.data
    } catch (error) {
        return { timeHours: 0 }
    }
}

export interface IDeleteAccountDemandData {
    email: string
    userName: string
    redirectSuccessLink: string
    redirectErrorLink: string
}

export const deleteAccountDemand = async (requestData: IDeleteAccountDemandData) => {
    try {
        await amsAPI.post('/api/user/deleteAccountDemand', requestData)
        return true
    } catch (error) {
        return errorHandler(error)
    }
}

const prepareFileToUpload = (file: File) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = (event: any) => {
            const arrayBuffer = event.target.result
            const byteArray = new Uint8Array(arrayBuffer)
            resolve(Array.from(byteArray))
        }

        reader.onerror = () => {
            reject(new Error('Could not read the selected file'))
        }

        reader.readAsArrayBuffer(file)
    })
}

export const uploadAvatar = async (file: File) => {
    try {
        const byteArray = await prepareFileToUpload(file)
        await amsAPI.put('/api/user/profile/avatar', {
            avatar: byteArray,
        })
        return true
    } catch (error) {
        return errorHandler(error)
    }
}

export const getUserFollowList = async () => {
    try {
        const response = await amsAPI.get('/api/user/follow')
        return Array.isArray(response.data) ? response.data : []
    } catch (error) {
        return []
    }
}

export const followUser = async (userId: string) => {
    try {
        await amsAPI.post('/api/user/follow', {
            id: userId,
        })
        return true
    } catch (error) {
        return errorHandler(error)
    }
}

export const unfollowUser = async (userId: string) => {
    try {
        await amsAPI.delete('/api/user/follow', {
            data: { id: userId },
        })
        return true
    } catch (error) {
        return errorHandler(error)
    }
}
